import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateWarehouseInput,
  UpdateWarehouseInput,
  Warehouse,
  WarehousesListResponse,
} from '@/features/inventory/types/warehouse.types';
import { apiFetch } from '@/lib/api';

/**
 * Warehouses — TanStack Query hooks.
 *
 * Mirrors the Slice 1 catalog pattern (brands, products, …). No
 * field-level gating on warehouses — every role sees the same
 * payload. Pagination is offset-based (1-based `page` for the API,
 * 0-based in TanStack Table state; the page converts +1 before
 * sending).
 */

export type WarehousesSortKey = 'name' | 'code';
export type SortDirection = 'asc' | 'desc';

export interface WarehousesListParams {
  page: number;
  pageSize: number;
  sort?: WarehousesSortKey;
  dir?: SortDirection;
  search?: string;
  includeInactive?: boolean;
}

function buildQuery(params: WarehousesListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.search) sp.set('search', params.search);
  if (params.includeInactive) sp.set('includeInactive', 'true');
  return sp.toString();
}

export const warehousesKeys = {
  all: ['warehouses'] as const,
  list: (params: WarehousesListParams) => ['warehouses', 'list', params] as const,
  detail: (id: string) => ['warehouses', 'detail', id] as const,
};

export function useWarehouses(params: WarehousesListParams) {
  return useQuery({
    queryKey: warehousesKeys.list(params),
    queryFn: () => apiFetch<WarehousesListResponse>(`/warehouses?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useWarehouse(id: string | null) {
  return useQuery({
    queryKey: warehousesKeys.detail(id ?? ''),
    queryFn: () => apiFetch<Warehouse>(`/warehouses/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWarehouseInput) =>
      apiFetch<Warehouse>('/warehouses', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: warehousesKeys.all }),
  });
}

export function useUpdateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWarehouseInput }) =>
      apiFetch<Warehouse>(`/warehouses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: warehousesKeys.all });
      qc.invalidateQueries({ queryKey: warehousesKeys.detail(id) });
    },
  });
}

export function useDeleteWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/warehouses/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: warehousesKeys.all }),
  });
}
